// src/pages/About.jsx
import {
  Box,
  Typography,
  AppBar,
  Stack,
  Button,
  Paper,
  Grid,
} from "@mui/material";
import { Link } from "react-router-dom";
import {
  Forest,
  EmojiObjects,
  Engineering,
  StarRate,
} from "@mui/icons-material";
import ContactUs from "../components/ContactUs";
import CertificatesSection from "../components/CertificatesSection";
import { Helmet } from "react-helmet-async";

import "../styles/About.css"; // <-- here we will add custom CSS for who-we-are

const About = () => {
  return (
    <>
      <Helmet>
        <title>About Us | KRM Landscape</title>
        <meta
          name="description"
          content="Learn about KRM Landscape, a professional landscaping company dedicated to creating sustainable and beautiful green spaces."
        />
        <meta
          name="keywords"
          content="KRM Landscape, Landscaping Company, Sustainable Landscaping, Green Spaces, Professional Landscapers"
        />
      </Helmet>

      <Box className="about-root">

        {/* Hero Section */}
        <Box className="about-main">
          <Box className="about-hero">
            <Typography variant="h3" className="hero-title">
              Welcome to <span className="highlight">KRM Landscape</span>
            </Typography>
            <Typography variant="h6" className="hero-subtitle">
              Creating sustainable outdoor spaces with beauty and purpose.
            </Typography>
            <Typography className="hero-description">
              From elegant gardens to functional landscapes, we design, build, and
              maintain green environments that thrive. Discover how nature and
              craftsmanship come together.
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
              alignItems="center"
              sx={{ mt: 3 }}  // adds margin-top (theme spacing * 3)
            >

              <Button
                component={Link}
                to="/services"
                variant="contained"
                className="btn-primary"
              >
                Our Services
              </Button>
              <Button
                component={Link}
                to="/contact"
                variant="outlined"
                className="btn-secondary"
              >
                Get in Touch
              </Button>
            </Stack>
          </Box>
        </Box>

        {/* ✅ Who We Are Section (Plain HTML + CSS) */}
        <section className="who-we-are">
          <div className="who-image">
            <img src="/images/ab.jpg" alt="KRM Landscape" />
          </div>
          <div className="who-text">
            <h2>Who We Are</h2>
            <p>
              At KRM Landscape, we are passionate about transforming spaces into
              vibrant, green, and welcoming environments. With expertise in plant
              supply, pot selection, indoor setups, kitchen gardens, and
              full-scale landscape maintenance, we bring nature closer to you in
              the most beautiful and sustainable way possible.
            </p>
            <p>
              From homes to offices, hotels to commercial complexes, we design
              solutions tailored to your space, style, and vision. Our commitment
              goes beyond just delivering plants or decor, we provide expert
              guidance, personalized service, and long-lasting results that
              enhance both aesthetics and well-being.
            </p>
            <p>
              Whether it’s creating a lush indoor corner, designing a functional
              kitchen garden, or maintaining a large outdoor landscape, we ensure
              every detail is handled with care and creativity. Our goal is
              simple: to help you connect with nature while elevating the beauty,
              functionality, and health of your surroundings.
            </p>
            <p>
              With KRM Landscape, you don’t just get greenery, you get a complete
              experience of growth, beauty, and sustainability.
            </p>
          </div>
        </section>

        {/* <CertificatesSection /> */}

        {/* Quote Box */}
        <Paper
          elevation={3}
          className="quote-box"
          sx={{
            mt: 4,
            maxWidth: "800px",
            mx: "auto",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" className="quote-text">
            "To inspire and shape environments where nature thrives in harmony
            with human experiences."
          </Typography>
        </Paper>

        {/* Our Mission, Vision & Goal */}
        <Box
          sx={{
            background: "linear-gradient(to right, #81c784, #e8f5e9)",
            padding: "2rem",
            borderRadius: "8px",
          }}
        >
          <Typography variant="h4" className="section-heading">
            Our Mission, Vision & Goal
          </Typography>
          <Grid container spacing={2} mt={2} mb={6} justifyContent="center">
            {[
              {
                title: "Our Mission",
                icon: <Forest fontSize="large" color="success" />,
                desc: "To build landscapes that breathe life into spaces while respecting nature’s balance.",
              },
              {
                title: "Our Vision",
                icon: <EmojiObjects fontSize="large" color="success" />,
                desc: "To be a national leader in green innovation and nature-first designs.",
              },
              {
                title: "Our Goal",
                icon: <Engineering fontSize="large" color="success" />,
                desc: "To deliver reliable, creative, and cost-effective landscaping solutions to every client.",
              },
            ].map((item, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                key={index}
                display="flex"
                justifyContent="center"
              >
                <Paper
                  elevation={4}
                  className="info-card"
                  sx={{
                    width: "100%",
                    maxWidth: 300,
                    textAlign: "center",
                    p: 3,
                  }}
                >
                  {item.icon}
                  <Typography variant="h6" fontWeight="bold" mt={2}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2">{item.desc}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* What We Do & Why Choose Us */}
        <Box
          sx={{
            background: "linear-gradient(to right, #e8f5e9, #ffffff)",
            paddingBottom: "2rem",
            paddingTop: "2rem",
            width: "100%",
          }}
        >
          <Typography variant="h4" className="section-heading">
            What We Do & Why Choose Us
          </Typography>
          <Grid container spacing={4} mt={2} mb={4} justifyContent="center">
            {[
              {
                title: "Personalized Designs",
                desc: "Each project is custom-built to reflect your personality and needs.",
              },
              {
                title: "Affordable Pricing",
                desc: "Cost-effective solutions that don’t compromise quality.",
              },
              {
                title: "Skilled Professionals",
                desc: "Experienced horticulturists and designers on every project.",
              },
              {
                title: "Timely Project Delivery",
                desc: "On-time execution with transparent milestones.",
              },
            ].map((item, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
                key={index}
                display="flex"
                justifyContent="center"
              >
                <Paper
                  elevation={4}
                  className="why-card"
                  sx={{
                    width: "100%",
                    maxWidth: 280,
                    textAlign: "center",
                    p: 2,
                  }}
                >
                  <StarRate fontSize="large" color="success" />
                  <Typography variant="h6" fontWeight="bold" mt={2}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2">{item.desc}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <ContactUs />
      </Box>
    </>
  );
};

export default About;
